"use client"

import type { FormEvent } from "react"
import { useState } from "react"
import {
  DialogTrigger as AriaDialogTrigger,
  Heading as AriaHeading,
} from "react-aria-components"

import { Button } from "@/components/ui/button"
import { CloseButton } from "@/components/ui/close-button"
import { Input } from "@/components/ui/input"
import { Dialog, Modal, ModalOverlay } from "@/components/ui/modal"
import { Select } from "@/components/ui/select"
import { TextArea } from "@/components/ui/textarea"
import {
  TRACK_PROMPT_CONTENT_TYPES,
  TRACK_PROMPT_CURATION_STATES,
  type TrackPromptContentType,
  type TrackPromptCurationState,
} from "@/types/contents"

export interface PromptFormValues {
  title: string
  guidance: string
  content_type: TrackPromptContentType
  curation_state: TrackPromptCurationState
  // Inertia's router.post/patch requires plain-object payloads.
  [key: string]: string
}

const CONTENT_TYPE_ITEMS = TRACK_PROMPT_CONTENT_TYPES.map((id) => ({
  id,
  label: id[0].toUpperCase() + id.slice(1),
}))

const CURATION_STATE_ITEMS = TRACK_PROMPT_CURATION_STATES.map((id) => ({
  id,
  label: id[0].toUpperCase() + id.slice(1),
}))

/**
 * Add/edit modal for a Collab's track prompts. Controlled by the parent —
 * it stays open on a validation failure (the parent only closes it on
 * success), so the entered values and field errors survive the redirect.
 * The curation-state select only shows in edit mode — new prompts always
 * start `pending` server-side, and there's nothing to lock/fill yet.
 */
export function PromptFormModal({
  isOpen,
  onOpenChange,
  mode,
  initialValues,
  errors,
  processing,
  onSubmit,
}: {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  mode: "add" | "edit"
  initialValues: PromptFormValues
  errors?: Record<string, string[]>
  processing: boolean
  onSubmit: (values: PromptFormValues) => void
}) {
  const [values, setValues] = useState(initialValues)

  const titleError = errors?.title?.[0]
  const guidanceError = errors?.guidance?.[0]
  const contentTypeError = errors?.content_type?.[0]
  const curationStateError = errors?.curation_state?.[0]

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    onSubmit(values)
  }

  return (
    <AriaDialogTrigger isOpen={isOpen} onOpenChange={onOpenChange}>
      <ModalOverlay isDismissable>
        <Modal>
          <Dialog>
            <div className="bg-primary relative w-full overflow-hidden rounded-2xl shadow-xl sm:max-w-md">
              <CloseButton
                onClick={() => onOpenChange(false)}
                theme="light"
                size="lg"
                className="absolute top-3 right-3"
              />

              <form onSubmit={handleSubmit} className="flex flex-col gap-5 p-6">
                <AriaHeading
                  slot="title"
                  className="text-md text-primary font-semibold"
                >
                  {mode === "add" ? "Add prompt" : "Edit prompt"}
                </AriaHeading>

                <Input
                  isRequired
                  label="Title"
                  placeholder="Morning routine"
                  value={values.title}
                  onChange={(value) => setValues({ ...values, title: value })}
                  isInvalid={!!titleError}
                  errorMessage={titleError}
                />

                <TextArea
                  label="Guidance"
                  hint={guidanceError}
                  isInvalid={!!guidanceError}
                  placeholder="What helps you start the day. (optional)"
                  value={values.guidance}
                  onChange={(value) =>
                    setValues({ ...values, guidance: value })
                  }
                  rows={3}
                />

                <Select
                  label="Content type"
                  size="sm"
                  isRequired
                  selectedKey={values.content_type}
                  items={CONTENT_TYPE_ITEMS}
                  onSelectionChange={(key) =>
                    key &&
                    setValues({
                      ...values,
                      content_type: key as TrackPromptContentType,
                    })
                  }
                >
                  {(item) => (
                    <Select.Item id={item.id}>{item.label}</Select.Item>
                  )}
                </Select>
                {contentTypeError && (
                  <p className="text-error-primary -mt-3 text-sm">
                    {contentTypeError}
                  </p>
                )}

                {mode === "edit" && (
                  <>
                    <Select
                      label="Status"
                      size="sm"
                      isRequired
                      selectedKey={values.curation_state}
                      items={CURATION_STATE_ITEMS}
                      onSelectionChange={(key) =>
                        key &&
                        setValues({
                          ...values,
                          curation_state: key as TrackPromptCurationState,
                        })
                      }
                    >
                      {(item) => (
                        <Select.Item id={item.id}>{item.label}</Select.Item>
                      )}
                    </Select>
                    {curationStateError && (
                      <p className="text-error-primary -mt-3 text-sm">
                        {curationStateError}
                      </p>
                    )}
                  </>
                )}

                <div className="flex justify-end gap-3 pt-1">
                  <Button
                    type="button"
                    color="secondary"
                    size="md"
                    onClick={() => onOpenChange(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    color="primary"
                    size="md"
                    isDisabled={processing}
                    isLoading={processing}
                  >
                    {mode === "add" ? "Add prompt" : "Save changes"}
                  </Button>
                </div>
              </form>
            </div>
          </Dialog>
        </Modal>
      </ModalOverlay>
    </AriaDialogTrigger>
  )
}
